import type { Theme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { getUserAvatarColor } from '../../../utils/avatarUtils';

export { getUserAvatarColor };

export const extractTitleTags = (rawTitle: string) => {
  if (!rawTitle) {
    return { cleanTitle: '', tags: '' };
  }

  const tagsArr: string[] = [];
  let cleanTitle = rawTitle;

  // 1. Match merge fields like {{ field_name }}
  const mergeFieldMatch = cleanTitle.match(/\{\{\s*[^}]+\s*\}\}\s*/g);
  if (mergeFieldMatch) {
    for (const m of mergeFieldMatch) {
      tagsArr.push(m.trim());
      cleanTitle = cleanTitle.replace(m, '');
    }
  }

  // Then extract anchor tags e.g. [ANS_001] or {CHK_001} or ANS_001
  const anchorTagMatch = cleanTitle.match(/(?:[[{]\s*)?(?:ANS|CHK)_\d+(?:\s*[\]}])?\s*/g);
  if (anchorTagMatch) {
    for (const m of anchorTagMatch) {
      tagsArr.push(m.trim());
      cleanTitle = cleanTitle.replace(m, '');
    }
  }

  // 3. Remove "INSERT" word (case-insensitive) and clean up whitespace
  cleanTitle = cleanTitle
    .replace(/\bINSERT\b/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return {
    cleanTitle: cleanTitle.trim(),
    tags: tagsArr.join(' '),
  };
};

export const restoreTitleTags = (cleanTitle: string, tags: string) => {
  if (!tags) {
    return cleanTitle.trim();
  }

  return `${tags.trim()} ${cleanTitle.trim()}`.trim();
};

export const countWords = (text: string) => {
  if (!text) {
    return 0;
  }
  // Strip HTML tags to get a more accurate count and reduce string size
  const stripped = text.replace(/<[^>]*>/g, ' ');
  let count = 0;
  let inWord = false;
  for (let i = 0; i < stripped.length; i++) {
    const code = stripped.charCodeAt(i);
    // Check for standard whitespace: space (32), tab (9), newline (10), carriage return (13), etc.
    // Including nbsp (160)
    if (code <= 32 || code === 160) {
      inWord = false;
    } else {
      if (!inWord) {
        count++;
        inWord = true;
      }
    }
  }
  return count;
};

export const cleanHtmlDisplay = (content: string) => {
  if (!content) {
    return '';
  }
  let cleaned = content;
  // Strip markdown code block wrappers if present (e.g. ```html ... ```)
  if (cleaned.trim().startsWith('```')) {
    cleaned = cleaned.replace(/^\s*```\w*\s*[\r\n]*/, '').replace(/[\r\n]*```\s*$/, '');
  }

  // Remove the first H1 tag if it exists at the very beginning of the content (or near it),
  // as it's redundant with the section title.
  // We use a non-greedy match to ensure we only hit the first one.
  cleaned = cleaned.trim();
  if (cleaned.toLowerCase().startsWith('<h1')) {
    cleaned = cleaned.replace(/<h1[^>]*>.*?<\/h1>/i, '').trim();
  }

  try {
    return DOMPurify.sanitize(cleaned);
  } catch (_e) {
    return ''; // Fallback to safe empty string
  }
};

export const getStatusColor = (
  status?: string,
  isAssigned?: boolean
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (isAssigned && status !== 'reviewed' && status !== 'complete') {
    return 'info';
  }
  switch (status) {
    case 'reviewed':
      return 'success';
    case 'complete':
      return 'warning';
    // case 'in_progress': return 'primary'; // Use custom sx for camel
    default:
      return 'default';
  }
};

export const getStatusLabel = (
  status?: string,
  hasSuggestion?: boolean,
  isAssigned?: boolean,
  isHistoryComparison?: boolean
) => {
  if (isHistoryComparison) {
    return 'History View';
  }
  if (hasSuggestion) {
    return 'Revised (AI)';
  }
  if (status === 'reviewed') {
    return 'Reviewed';
  }
  if (status === 'complete') {
    return 'Complete';
  }
  if (status === 'in_progress') {
    return 'In Progress';
  }
  if (isAssigned) {
    return 'Assigned';
  }
  return 'Pending';
};

export const getStatusSx = (
  theme: Theme,
  status?: string,
  hasSuggestion?: boolean,
  isAssigned?: boolean,
  isHistoryComparison?: boolean
) => {
  const baseSx = { mr: 1, fontWeight: 'bold', minWidth: '100px', fontSize: '0.8125rem' };

  if (isHistoryComparison) {
    return {
      ...baseSx,
      bgcolor: theme.palette.secondary.main,
      color: 'white',
    };
  }

  if (hasSuggestion) {
    return {
      ...baseSx,
      bgcolor: theme.palette.bidlogiq.rust,
      color: 'white',
    };
  }

  if (status === 'in_progress') {
    return {
      ...baseSx,
      bgcolor: theme.palette.bidlogiq.camel,
      color: 'common.black',
      '&:hover, &:focus': {
        bgcolor: theme.palette.bidlogiq.camel,
        filter: 'brightness(0.9)',
      },
    };
  }

  if (status === 'complete') {
    return {
      ...baseSx,
      bgcolor: theme.palette.warning.dark,
      color: theme.palette.getContrastText(theme.palette.warning.dark),
    };
  }

  if (isAssigned && status !== 'reviewed' && status !== 'complete') {
    return {
      ...baseSx,
      bgcolor: theme.palette.info.main,
      color: theme.palette.getContrastText(theme.palette.info.main),
    };
  }

  return baseSx;
};

// Helper for excluded sections
export const isExcluded = (title: string) => {
  const lowerTitle = (title || '').toLowerCase();
  return lowerTitle.includes('executive summary') || lowerTitle.includes('case stud');
};

export const getFileId = (title: string) => {
  if (!title) {
    return null;
  }
  const match = title.match(/\[([^\]]+)\](?: *)$/);
  if (match?.[1] && !match[1].startsWith('ANS_') && !match[1].startsWith('CHK_')) {
    return match[1];
  }
  if (title.includes('ANS_') || title.includes('CHK_')) {
    return 'Returnable Form';
  }
  return null;
};
